/**
 * @file useLogin.js
 * @description 登录弹窗逻辑（手机号 + 滑块 + 短信验证码）
 * @module features/user/composables/useLogin
 */

import { computed, nextTick, ref, watch } from 'vue'
import {
  isAuthVerifyFieldError,
  resolveAuthVerifyErrorMessage,
} from '@/shared/utils/apiErrorCodes'
import { useUserStore } from '@/stores/user'
import { sendSmsApi, signInApi } from '@/features/user/api/userApi'
import { isWeChatBrowser } from '@/shared/utils/platform'
import { isOAuthInFlight } from '@/shared/utils/wxMpOAuth'
import { buildStoredSession, saveAuthUser } from '@/shared/utils/auth'

const SMS_COOLDOWN_SECONDS = 60
const MODULE_CODE = 'COMIC-WEB'

/**
 * @param {{ showLogin: boolean }} props
 * @param {(event: 'close' | 'show-register' | 'login-success', payload?: unknown) => void} emit
 */
export function useLogin(props, emit) {
  const mobile = ref('')
  const verifyCode = ref('')
  const mobileError = ref('')
  const verifyCodeError = ref('')
  const verifyCodeFieldError = ref('')
  const apiError = ref('')
  const isLoggingIn = ref(false)
  const loginSuccess = ref(false)
  const showErrorPopup = ref(false)
  const sendCodeCountdown = ref(0)
  const isVerified = ref(false)

  const mobileRef = ref(null)
  const verifyCodeRef = ref(null)
  const sliderVerifyRef = ref(null)

  const mobileInputClass = computed(() => (mobileError.value ? 'error-input' : ''))

  const verifyCodeInputClass = computed(() => {
    const classes = []
    if (verifyCodeError.value || verifyCodeFieldError.value) {
      classes.push('error-input')
    }
    if (verifyCodeFieldError.value) {
      classes.push('auth-verify-api-error')
    }
    return classes.join(' ')
  })

  const sendCodeDisabled = computed(
    () =>
      sendCodeCountdown.value > 0 ||
      !/^1\d{10}$/.test(mobile.value.trim()) ||
      !isVerified.value
  )

  const loginButtonDisabled = computed(() => {
    if (isLoggingIn.value || loginSuccess.value) return true
    const m = mobile.value.trim()
    const code = verifyCode.value.trim()
    return !/^1\d{10}$/.test(m) || !/^\d{6}$/.test(code)
  })

  const sendCodeBtnText = computed(() => {
    if (sendCodeCountdown.value > 0) {
      return `${sendCodeCountdown.value}秒后重发`
    }
    return '获取验证码'
  })

  function triggerErrorAnimation(field) {
    const inputField =
      field === 'mobile' ? mobileRef.value : verifyCodeRef.value
    if (inputField) {
      inputField.classList.add('input-error')
      setTimeout(() => inputField.classList.remove('input-error'), 300)
    }
  }

  function hideErrorPopupAfterDelay() {
    setTimeout(() => {
      showErrorPopup.value = false
    }, 2000)
  }

  function applySmsOrLoginApiError(payload) {
    const code = payload?.code
    const msg = payload?.msg || payload?.errmsg
    if (isAuthVerifyFieldError(code)) {
      verifyCodeFieldError.value = resolveAuthVerifyErrorMessage(code, msg)
      showErrorPopup.value = false
      return
    }
    verifyCodeFieldError.value = ''
    apiError.value = msg || '发送失败'
    showErrorPopup.value = true
    hideErrorPopupAfterDelay()
  }

  function applyLoginFailure(payload) {
    const code = payload?.code
    const msg = payload?.msg || payload?.errmsg
    if (isAuthVerifyFieldError(code)) {
      verifyCodeFieldError.value = resolveAuthVerifyErrorMessage(code, msg)
      showErrorPopup.value = false
      return
    }
    verifyCodeFieldError.value = ''
    apiError.value = msg || '登录失败，请稍后重试'
    showErrorPopup.value = true
    hideErrorPopupAfterDelay()
  }

  function startSendCodeCountdown() {
    sendCodeCountdown.value = SMS_COOLDOWN_SECONDS
    const timer = setInterval(() => {
      sendCodeCountdown.value--
      if (sendCodeCountdown.value <= 0) clearInterval(timer)
    }, 1000)
  }

  async function sendVerifyCode() {
    const mobileValue = mobile.value.trim()
    if (!isVerified.value) return
    if (!/^1\d{10}$/.test(mobileValue)) {
      mobileError.value = '请输入正确的手机号'
      return
    }
    mobileError.value = ''
    try {
      const response = await sendSmsApi({ mobile: mobileValue })
      if (response.data.code === 200 || response.data.code === 0) {
        verifyCodeFieldError.value = ''
        startSendCodeCountdown()
      } else {
        applySmsOrLoginApiError(response.data)
      }
    } catch (error) {
      const data = error.response?.data
      if (data) {
        applySmsOrLoginApiError(data)
      } else {
        verifyCodeFieldError.value = ''
        apiError.value = '网络错误，请稍后再试'
        showErrorPopup.value = true
        hideErrorPopupAfterDelay()
      }
    }
  }

  async function login() {
    try {
      const response = await signInApi({
        moduleCode: MODULE_CODE,
        mobile: mobile.value.trim(),
        verifyCode: verifyCode.value.trim(),
      })
      if (response.data.code === 200 || response.data.code === 0) {
        const user = response.data.data
        const userStore = useUserStore()
        const tokenData = buildStoredSession(user)
        userStore.setUser(tokenData)
        saveAuthUser(tokenData)
        isLoggingIn.value = false
        loginSuccess.value = true
        setTimeout(() => {
          emit('login-success', tokenData)
          loginSuccess.value = false
          if (sliderVerifyRef.value) {
            sliderVerifyRef.value.reset()
            isVerified.value = false
          }
        }, 1000)
        if (!isWeChatBrowser() && !isOAuthInFlight()) {
          setTimeout(() => location.reload(), 1000)
        }
      } else {
        applyLoginFailure(response.data)
      }
    } catch (error) {
      const data = error.response?.data
      if (data) {
        applyLoginFailure(data)
      } else {
        verifyCodeFieldError.value = ''
        apiError.value = '网络错误，请稍后再试'
        showErrorPopup.value = true
        hideErrorPopupAfterDelay()
      }
    } finally {
      isLoggingIn.value = false
    }
  }

  function validateLogin() {
    if (isLoggingIn.value || loginSuccess.value) return
    mobileError.value = ''
    verifyCodeError.value = ''
    verifyCodeFieldError.value = ''
    if (!mobile.value.trim()) {
      mobileError.value = '请输入手机号'
      triggerErrorAnimation('mobile')
    } else if (!/^1\d{10}$/.test(mobile.value.trim())) {
      mobileError.value = '请输入正确的手机号'
      triggerErrorAnimation('mobile')
    }
    if (!verifyCode.value.trim()) {
      verifyCodeError.value = '请输入验证码'
      triggerErrorAnimation('verifyCode')
    } else if (!/^\d{6}$/.test(verifyCode.value.trim())) {
      verifyCodeError.value = '验证码为6位数字'
      triggerErrorAnimation('verifyCode')
    }
    if (mobileError.value || verifyCodeError.value) return
    isLoggingIn.value = true
    login()
  }

  function handleVerifySuccess() {
    isVerified.value = true
  }

  function resetSliderVerify() {
    if (sliderVerifyRef.value) {
      sliderVerifyRef.value.reset()
      isVerified.value = false
    }
  }

  watch(verifyCode, () => {
    verifyCodeFieldError.value = ''
  })

  watch(
    () => props.showLogin,
    (newVal) => {
      if (!newVal) {
        sendCodeCountdown.value = 0
        verifyCodeFieldError.value = ''
        resetSliderVerify()
      }
    }
  )

  watch(mobile, () => {
    verifyCodeFieldError.value = ''
    if (!isVerified.value) return
    nextTick(() => {
      resetSliderVerify()
    })
  })

  return {
    mobile,
    verifyCode,
    mobileError,
    verifyCodeError,
    verifyCodeFieldError,
    apiError,
    isLoggingIn,
    loginSuccess,
    showErrorPopup,
    sendCodeCountdown,
    isVerified,
    mobileRef,
    verifyCodeRef,
    sliderVerifyRef,
    mobileInputClass,
    verifyCodeInputClass,
    sendCodeDisabled,
    loginButtonDisabled,
    sendCodeBtnText,
    validateLogin,
    sendVerifyCode,
    handleVerifySuccess,
  }
}
